/**
 * Pure schedule-builder form logic: validation, rule mapping, dosage
 * composition. No I/O — unit-testable in isolation.
 */
import type { HealthItemType, RecurrenceRule, RecurrenceType } from '@/types'

export type ScheduleFormState = {
  name: string
  healthType: HealthItemType
  /** Free-text amount, e.g. '400'. Combined with `unit` into the dosage. */
  dosage: string
  /** Free-text unit or description, e.g. 'mg', 'capsule', 'ml'. */
  unit: string
  quantity: number
  /** yyyy-mm-dd */
  startDate: string
  hasEndDate: boolean
  /** yyyy-mm-dd — only meaningful when hasEndDate is true. */
  endDate: string
  /** HH:mm */
  localTime: string
  timezone: string
  recurrenceType: RecurrenceType
  /** 0 = Sunday … 6 = Saturday. Weekly only. */
  weekdays: number[]
  /** 1–31. Monthly only. */
  dayOfMonth: number
  instructions: string
}

export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
export const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/** Composes the stored dosage string from amount + free-text unit. */
export function composeDosage(form: Pick<ScheduleFormState, 'dosage' | 'unit'>): string {
  return [form.dosage.trim(), form.unit.trim()].filter(Boolean).join(' ')
}

/** Maps the form recurrence fields to a RecurrenceRule. */
export function ruleFromForm(
  form: Pick<ScheduleFormState, 'recurrenceType' | 'weekdays' | 'dayOfMonth'>,
): RecurrenceRule {
  switch (form.recurrenceType) {
    case 'once':
      return { type: 'once' }
    case 'daily':
      return { type: 'daily' }
    case 'weekly':
      return { type: 'weekly', weekdays: [...new Set(form.weekdays)].sort((a, b) => a - b) }
    case 'monthly':
      return { type: 'monthly', dayOfMonth: form.dayOfMonth }
  }
}

/**
 * Returns a list of Dutch validation messages. Empty list = valid.
 * Dosage is required for supplements and medication, optional for 'other'.
 */
export function validateScheduleForm(form: ScheduleFormState): string[] {
  const errors: string[] = []

  if (!form.name.trim()) {
    errors.push('Vul een naam in.')
  }
  if (form.healthType !== 'other' && !composeDosage(form)) {
    errors.push('Vul een dosering in.')
  }
  if (!DATE_PATTERN.test(form.startDate)) {
    errors.push('Kies een geldige startdatum.')
  }
  if (form.hasEndDate) {
    if (!DATE_PATTERN.test(form.endDate)) {
      errors.push('Kies een geldige einddatum.')
    } else if (DATE_PATTERN.test(form.startDate) && form.endDate < form.startDate) {
      errors.push('De einddatum ligt vóór de startdatum.')
    }
  }
  if (!TIME_PATTERN.test(form.localTime)) {
    errors.push('Kies een geldig tijdstip.')
  }
  if (!form.timezone.trim()) {
    errors.push('Vul een tijdzone in.')
  }
  if (form.recurrenceType === 'weekly' && form.weekdays.length === 0) {
    errors.push('Kies minstens één weekdag.')
  }
  if (
    form.recurrenceType === 'monthly' &&
    (!Number.isInteger(form.dayOfMonth) || form.dayOfMonth < 1 || form.dayOfMonth > 31)
  ) {
    errors.push('Kies een dag van de maand (1 t/m 31).')
  }
  if (!Number.isInteger(form.quantity) || form.quantity < 1) {
    errors.push('De hoeveelheid moet minimaal 1 zijn.')
  }

  return errors
}
